import { CODE_SUCCESS, SESSION_KEY } from '@/constants/api';
import { getCookie } from '@/utils/cookie';
import { toast } from '@/utils/toast';
import createClient from 'openapi-fetch';
import qs from 'qs';
import { paths } from './types';

// 获取当前语言的函数
const getCurrentLanguage = () => {
  if (typeof window === 'undefined') return 'en';

  // 从 URL 路径中获取语言
  const pathname = window.location.pathname;
  const firstSegment = pathname.split('/').filter(Boolean)[0];

  // 检查第一个路径段是否是支持的语言
  return firstSegment === 'zh' || firstSegment === 'en' ? firstSegment : 'en';
};

const generateUA = () => {
  if (typeof window === 'undefined') {
    return `en|web|0.1.92|0x0|node`;
  }

  // 动态获取语言
  const language = getCurrentLanguage();

  // 获取客户端类型
  const clientType = 'web';

  // 版本号
  const version = '0.1.92';

  // 获取分辨率
  const resolution = `${window.screen.width}x${window.screen.height}`;

  // 获取设备类型
  let deviceType = 'web';

  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
    deviceType = 'ios';
  } else if (userAgent.includes('android')) {
    deviceType = 'android';
  } else if (userAgent.includes('windows')) {
    deviceType = 'windows';
  }

  return `${language}|${clientType}|${version}|${resolution}|${deviceType}`;
};

const sdk = createClient<paths>({
  baseUrl: process.env.NEXT_PUBLIC_CC_API,
  headers: {
    Accept: 'application/json',
  },
  querySerializer(params) {
    return qs.stringify(params, {
      encodeValuesOnly: true,
    });
  },
});

if (typeof window !== 'undefined') {
  sdk.use({
    onRequest: (config) => {
      // 动态设置 UA 头，否则 UA 生成逻辑在初始化时就固定了，无法动态获取当前语言
      const currentUA = generateUA();
      config.request.headers.set('UA', currentUA);

      // 根据路径前缀选择适当的认证令牌
      const url = config.request.url;

      if (url.includes('/api/admin/')) {
        // 管理员路径使用管理员令牌
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
          config.request.headers.set('Authorization', adminToken);
        }
      } else {
        // 普通用户路径使用普通用户令牌
        const userToken = getCookie(SESSION_KEY);
        if (userToken) {
          config.request.headers.set('Authorization', userToken);
        }
      }
    },
    onResponse: async ({ response }) => {
      const res = response.clone();
      const data = await res.json();

      // url 白名单
      const urlWhiteList = ['/api/generate/image/extend'];

      if (urlWhiteList.some((url) => res.url.includes(url))) {
        return;
      }

      if (data && 'code' in data) {
        const code = data.code as string;

        // 定义需要触发订阅动画的错误代码列表
        const subscribeAnimationCodes = [
          'api.task_concurrent_limit',
          'api.user_image_count_too_many',
          // 可以在这里添加其他需要触发订阅动画的错误代码
        ];

        // 定义不需要显示 toast 的错误代码列表（包含订阅动画错误）
        const silentErrorCodes = [
          'api.image_publish_rejected',
          ...subscribeAnimationCodes, // 🎯 订阅动画错误不显示普通 toast
          // 可以在这里添加其他需要静默处理的错误代码
        ];

        if (code && code !== CODE_SUCCESS && !silentErrorCodes.includes(code)) {
          toast.error(code);
        }

        // 🎯 全局错误处理：检查是否需要触发订阅动画
        if (code && code !== CODE_SUCCESS && subscribeAnimationCodes.includes(code)) {
          window.dispatchEvent(
            new CustomEvent('triggerSubscribeAnimation', {
              detail: {
                startPosition: null, // API 触发时没有起点位置
                errorMessage: code,
              },
            }),
          );
        }
      }
      return undefined;
    },
  });
}

export default sdk;
